/**
 * Shared theme tokens for both web and mobile implementations
 *
 * These tokens map to daisyUI semantic color names for the web
 * implementation and provide consistent naming for the mobile theme.
 */

/**
 * Core semantic color tokens
 * Aligned with daisyUI's color naming convention
 */
export interface ThemeTokens {
  // Base colors
  background: string; // Main app background (base-100)
  surfacePrimary: string; // Primary surface (base-200)
  surfaceSecondary: string; // Secondary surface (base-300)

  // Text colors
  textPrimary: string; // Main text (base-content)
  textSecondary: string; // Secondary/less important text

  // Brand colors - matches daisyUI
  primary: string; // Primary brand color
  primaryContent: string; // Content on primary color
  secondary: string; // Secondary brand color
  secondaryContent: string; // Content on secondary color
  accent: string; // Accent brand color
  accentContent: string; // Content on accent color
  neutral: string; // Neutral color for UI
  neutralContent: string; // Content on neutral color

  // Status colors - matches daisyUI
  info: string; // Informational
  infoContent: string; // Content on info color
  success: string; // Success/completion
  successContent: string; // Content on success color
  warning: string; // Warning/caution
  warningContent: string; // Content on warning color
  error: string; // Error/danger
  errorContent: string; // Content on error color

  // Fretboard-specific colors
  fretboardBackground: string; // Background of fretboard area
  fretboardWood: string; // Wood color for fretboard
  fretColor: string; // Color of frets
  nutColor: string; // Color of nut (fret 0)
  stringColorTreble: string; // Color of treble/higher strings
  stringColorBass: string; // Color of bass/lower strings
  markerColor: string; // Base color for standard fret markers
  markerSpecialColor: string; // Color for special markers (octaves)

  // Fret metal gradient colors
  fretMetalLight: string; // Light color for fret metal gradient
  fretMetalMid: string; // Middle color for fret metal gradient
  fretMetalDark: string; // Dark color for fret metal gradient

  // Pearl inlay gradient colors
  pearlInlay: string[]; // 5 colors for pearl inlay gradient

  // Abalone gradient colors
  abalone: string[]; // 6 colors for abalone gradient

  // Border colors
  border: string; // Border color for components
}

const FRETBOARD_COLORS = {
  // Fretboard-specific colors
  fretboardBackground: '#f9f6f1',
  fretboardWood: '#59452d', // Wood grain from FretboardGradients.tsx
  fretColor: '#444444', // From FretboardFrets.tsx
  nutColor: '#f8f8f8', // From FretboardFrets.tsx
  stringColorTreble: 'rgba(220, 220, 220, 0.9)', // From FretboardStrings.tsx
  stringColorBass: 'rgba(220, 200, 170, 0.9)', // From FretboardStrings.tsx
  markerColor: '#d0d0d0', // Based on pearl inlay gradient from FretboardGradients.tsx
  markerSpecialColor: '#305860', // Based on abalone gradient from FretboardGradients.tsx

  // Fret metal gradient colors
  fretMetalLight: '#a0a0a0', // Light color for fret metal gradient
  fretMetalMid: '#808080', // Middle color for fret metal gradient
  fretMetalDark: '#606060', // Dark color for fret metal gradient

  // Pearl inlay gradient colors
  pearlInlay: ['#ffffff', '#f5f5f5', '#e0e0e0', '#d8d8d8', '#d0d0d0'],

  // Abalone gradient colors
  abalone: ['#c0e8f5', '#a0d8e0', '#80c0c8', '#60a8b0', '#407878', '#305860'],
};

/**
 * Light theme tokens - based on daisyUI "cupcake" theme
 */
export const lightThemeTokens: ThemeTokens = {
  // Base colors
  background: '#f9f6f1', // base-100 (cupcake)
  surfacePrimary: '#f3eeea', // base-200 (cupcake)
  surfaceSecondary: '#ebe4dd', // base-300 (cupcake)

  // Text colors
  textPrimary: '#433A3F', // base-content (cupcake)
  textSecondary: '#6e6167', // lighter version of base-content

  // Brand colors
  primary: '#b4e9d6', // primary (cupcake)
  primaryContent: '#60a5a7', // primary-content (cupcake)
  secondary: '#fbd3e1', // secondary (cupcake)
  secondaryContent: '#d76f83', // secondary-content (cupcake)
  accent: '#f2d0a4', // accent (cupcake)
  accentContent: '#c18659', // accent-content (cupcake)
  neutral: '#3b3437', // neutral (cupcake)
  neutralContent: '#eceaeb', // neutral-content (cupcake)

  // Status colors
  info: '#8ad6f2', // info (cupcake)
  infoContent: '#3b6d87', // info-content (cupcake)
  success: '#a5e0c5', // success (cupcake)
  successContent: '#2d6244', // success-content (cupcake)
  warning: '#fcd777', // warning (cupcake)
  warningContent: '#7f5f14', // warning-content (cupcake)
  error: '#ee9e9c', // error (cupcake)
  errorContent: '#782928', // error-content (cupcake)
  // Border colors
  border: '#e3d9cd', // Slightly darker than base-200

  ...FRETBOARD_COLORS,
};

/**
 * Dark theme tokens - based on daisyUI "dark" theme
 */
export const darkThemeTokens: ThemeTokens = {
  // Base colors
  background: '#2a303c', // base-100 (dark)
  surfacePrimary: '#242933', // base-200 (dark)
  surfaceSecondary: '#1f242d', // base-300 (dark)

  // Text colors
  textPrimary: '#f8f9fc', // base-content (dark)
  textSecondary: '#a6adbb', // lighter version of base-content

  // Brand colors
  primary: '#9747ff', // primary (dark)
  primaryContent: '#f2f2fa', // primary-content (dark)
  secondary: '#f872a3', // secondary (dark)
  secondaryContent: '#f5f5f8', // secondary-content (dark)
  accent: '#acecfa', // accent (dark)
  accentContent: '#4c555e', // accent-content (dark)
  neutral: '#1e2023', // neutral (dark)
  neutralContent: '#eceaeb', // neutral-content (dark)

  // Status colors
  info: '#a4cafe', // info (dark)
  infoContent: '#3b6d87', // info-content (dark)
  success: '#b9f5d0', // success (dark)
  successContent: '#4b725c', // success-content (dark)
  warning: '#fee08a', // warning (dark)
  warningContent: '#745823', // warning-content (dark)
  error: '#f9a8a8', // error (dark)
  errorContent: '#782928', // error-content (dark)
  // Border colors
  border: '#3d4352', // Slightly darker than base-200

  ...FRETBOARD_COLORS,
};
